/**
 * Sprint 18 B7: Rebooking Intelligence — AI-suggested rebooking opportunities
 */
import { useState } from "react";
import { AlertTriangle, Brain, Calendar, ChevronRight, Loader2, Siren, Zap } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { Skeleton } from "@/components/skeleton";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatDate, formatWeekdayMonthDay } from "@/lib/i18n";

interface RebookingSuggestion {
  id: string;
  personId: string;
  customerName: string;
  lastVisit: string;
  avgIntervalDays: number;
  daysSinceLastVisit: number;
  serviceId: string;
  serviceName: string;
  suggestedDate: string;
  suggestedProviderId: string | null;
  suggestedProviderName: string | null;
  confidence: number;
  overdueByDays: number;
  engagementTrend: "increasing" | "stable" | "declining";
  churnSignals: Array<{
    signalType: string;
    severity: string;
    description: string;
    suggestedAction: string;
  }>;
  reminderAction: {
    label: string;
  };
}

function formatSuggestedBookingLabel(suggestion: RebookingSuggestion): string {
  const dateLabel = formatWeekdayMonthDay(suggestion.suggestedDate);

  if (suggestion.suggestedProviderName) {
    return `${dateLabel} · ${suggestion.suggestedProviderName}`;
  }

  return dateLabel;
}

export default function BookingRebookingPage() {
  const { data, isLoading, error } = useApi<{ suggestions: RebookingSuggestion[] }>("/crm/intelligence/bookings/rebooking");
  const [pendingReminderId, setPendingReminderId] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const suggestions = data?.suggestions ?? [];

  async function handleSendReminder(suggestion: RebookingSuggestion) {
    setPendingReminderId(suggestion.id);
    setActionFeedback(null);
    try {
      const result = await apiFetch<{ data: { workflowId: string; enrollmentId: string } }>(
        `/crm/intelligence/bookings/rebooking/${encodeURIComponent(suggestion.personId)}/${encodeURIComponent(suggestion.serviceId)}/remind`,
        {
          method: "POST",
          body: JSON.stringify({
            suggestedDate: suggestion.suggestedDate,
            avgIntervalDays: suggestion.avgIntervalDays,
            overdueByDays: suggestion.overdueByDays,
            customerName: suggestion.customerName,
            serviceName: suggestion.serviceName,
            suggestedProviderName: suggestion.suggestedProviderName,
            rebookingUrl: `/portal/book?service=${encodeURIComponent(suggestion.serviceId)}&date=${encodeURIComponent(suggestion.suggestedDate)}${suggestion.suggestedProviderId ? `&provider=${encodeURIComponent(suggestion.suggestedProviderId)}` : ""}`,
          }),
        },
      );
      setActionFeedback(`Reminder workflow started (${result.data.enrollmentId}).`);
    } catch (requestError) {
      setActionFeedback(
        requestError instanceof ApiError
          ? requestError.message
          : requestError instanceof Error
            ? requestError.message
            : "Failed to trigger reminder workflow.",
      );
    } finally {
      setPendingReminderId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Rebooking Intelligence</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">Customers due for a follow-up — AI-ranked by likelihood and value.</p>
      </div>

      {error ? (
        <div className="rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-4 text-sm text-[var(--color-foreground)]">
          {error}
        </div>
      ) : null}

      {actionFeedback ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-foreground)]">
          {actionFeedback}
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : suggestions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-12 text-center">
          <Brain className="h-10 w-10 mx-auto text-[var(--color-ai)] mb-3" />
          <p className="text-[var(--color-foreground)] font-medium">No rebooking suggestions</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Suggestions appear when customers are overdue for follow-ups.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => {
            const overdue = s.daysSinceLastVisit > s.avgIntervalDays;
            return (
              <div key={s.id} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--color-foreground)]">{s.customerName}</span>
                      {overdue && (
                        <span className="rounded-full bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-warning)]">
                          {s.overdueByDays}d overdue
                        </span>
                      )}
                      <span className="text-xs text-[var(--color-ai)]">{s.confidence}% confident</span>
                    </div>
                    <div className="text-sm text-[var(--color-muted-foreground)] mt-1">
                      {s.serviceName} · Last visit {formatDate(s.lastVisit)} · Avg every {s.avgIntervalDays}d
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.engagementTrend === "declining" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-error)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-error)]">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Declining frequency
                        </span>
                      ) : null}
                      {s.churnSignals.map((signal) => (
                        <span
                          key={`${s.id}:${signal.signalType}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[var(--color-ai)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-ai)]"
                        >
                          <Siren className="h-3.5 w-3.5" />
                          {signal.description}
                        </span>
                      ))}
                    </div>
                    {s.churnSignals.length > 0 ? (
                      <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-muted-foreground)]">
                        <div className="font-medium text-[var(--color-foreground)]">Recommended action</div>
                        <div className="mt-1">{s.churnSignals[0]?.suggestedAction}</div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                    >
                      <Calendar className="h-4 w-4" />
                      Book {formatSuggestedBookingLabel(s)}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSendReminder(s)}
                      disabled={pendingReminderId === s.id}
                      className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-60"
                    >
                      {pendingReminderId === s.id ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Triggering workflow
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4" />
                          {s.reminderAction.label}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
